/**
 * V108-8-1 (staging review 2026-10-13): one-button GitHub connect.
 *
 *   ConnectGitHubButton   "Connect GitHub" → the GAC Pro app's install / configure page on GitHub (or GitHub's sign-in,
 *                         until the app asks for authorization during installation); no repository is typed first
 *   GitHubRepoPicker      back in GAC Pro: the repositories the installation can read, as checkboxes; "Add repositories
 *                         on GitHub" opens the installation's settings for a repository that isn't there yet
 *   GitHubReturnAlert     one message per failure reason, each with its one action (never a dead end)
 */
import { useId, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Field, Input, Radio, Spinner } from '@aigovops/design-system';
import { addGitHubRepos, authorizeGitHubConnection, getGitHubRepos, saveGitHubRepos } from '../lib/lifecycle-api';
import { ApiError } from '../lib/api';
import { githubReturnMessage, type GitHubReturnAction } from './ReconnectGitHub';
import { ExtLink } from './setup/parts';

export interface GitHubStatus { configured: boolean; install_url: string | null; flow?: 'install' | 'oauth' }

/** This page, without GitHub's return params (where a start comes back to). */
export function currentReturnPath(): string {
  const u = new URL(window.location.href);
  for (const k of ['github', 'connector', 'reason', 'owner']) u.searchParams.delete(k);
  return `${u.pathname}${u.search}`;
}

const startError = (e: unknown) => e instanceof ApiError && e.status === 403 ? 'Only a workspace owner or admin can connect GitHub.'
  : e instanceof ApiError && e.status === 503 ? 'GitHub is not available in this workspace.' : 'GitHub could not be opened. Try again.';

/** Starts a connection: GitHub shows Install (or Configure, when the app is already on that account), then comes back. */
export function ConnectGitHubButton({ slug, label = 'Connect GitHub', variant = 'secondary', returnTo, describedBy }: {
  slug: string; label?: string; variant?: 'primary' | 'secondary' | 'ghost'; returnTo?: string; describedBy?: string;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const busyRef = useRef(false);
  const errId = useId();
  const start = async () => {
    if (busyRef.current) return;
    busyRef.current = true; setBusy(true); setErr(null);
    try { window.location.assign((await authorizeGitHubConnection(slug, returnTo)).url); }
    catch (e) { setErr(startError(e)); busyRef.current = false; setBusy(false); }
  };
  return <span className="gac-reconnect">
    <Button type="button" variant={variant} aria-disabled={busy || undefined} aria-busy={busy || undefined} data-part="connect-github"
      aria-describedby={[describedBy, err ? errId : null].filter(Boolean).join(' ') || undefined} onClick={() => void start()}>{busy ? 'Opening GitHub…' : label}</Button>
    {err ? <span id={errId} role="alert" className="gac-reconnect-err">{err}</span> : null}
  </span>;
}

/** A return message's one action: connect again, or the app's page on GitHub (install / configure). */
function ReturnAction({ slug, action, status, returnTo }: { slug: string; action: GitHubReturnAction; status: GitHubStatus | null; returnTo?: string }) {
  // With the install flow, Connect IS the app's install / configure page, so it carries the state back here.
  if (action.kind === 'install' && status?.flow !== 'install' && status?.install_url) {
    return <><ExtLink href={status.install_url} variant="primary">{action.label}</ExtLink>
      <ConnectGitHubButton slug={slug} label="Installed? Connect GitHub" returnTo={returnTo} /></>;
  }
  return <ConnectGitHubButton slug={slug} label={action.label} variant="primary" returnTo={returnTo} />;
}

/** Back from GitHub with `?github=failed&reason=…`: what happened, in words, and the one thing to do. */
export function GitHubReturnAlert({ slug, github, reason, owner, merged, status, returnTo, suffix, onDismiss }: {
  slug: string; github: string | null; reason: string | null; owner: string | null; merged?: string | null; status: GitHubStatus | null; returnTo?: string; suffix?: string; onDismiss?: () => void;
}) {
  const msg = githubReturnMessage(github, reason, owner, merged);
  if (!msg) return null;
  return <Alert variant={msg.ok ? 'success' : 'error'} onDismiss={onDismiss}>
    <p>{msg.text}{suffix ? ` ${suffix}` : ''}</p>
    {msg.action && status?.configured !== false && <div className="su-acts"><ReturnAction slug={slug} action={msg.action} status={status} returnTo={returnTo} /></div>}
  </Alert>;
}

/** Pick the repositories to scan, from what the GAC Pro app can read on that account. */
export function GitHubRepoPicker({ slug, connectorId, onSaved, onCancel }: {
  slug: string; connectorId: string; onSaved: (connectorId: string, result: 'connected' | 'reconnected' | 'updated') => void; onCancel?: () => void;
}) {
  const q = useQuery({ queryKey: ['github-repos', slug, connectorId], queryFn: () => getGitHubRepos(slug, connectorId), retry: false, refetchOnWindowFocus: true });
  const [account, setAccount] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Record<string, Set<string>>>({});
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const headingId = useId();
  const data = q.data;
  const inst = data ? (data.installations.find((i) => i.account === account) ?? data.installations[0]) : undefined;
  const picked = useMemo(() => {
    if (!inst) return new Set<string>();
    if (chosen[inst.account]) return chosen[inst.account]!;
    const sel = inst.repositories.filter((r) => r.selected).map((r) => r.full_name);
    return new Set(sel.length ? sel : inst.repositories.length === 1 ? [inst.repositories[0]!.full_name] : []);
  }, [inst, chosen]);

  if (q.isLoading) return <Spinner label="Loading your GitHub repositories" />;
  if (q.error || !data || !inst) {
    const gone = q.error instanceof ApiError && q.error.status === 404;
    return <Alert variant="error"><p>{gone ? 'This GitHub sign-in expired before repositories were picked.' : 'Your GitHub repositories could not be loaded.'}</p>
      <div className="su-acts">{gone ? <ConnectGitHubButton slug={slug} label="Connect GitHub again" variant="primary" /> : <Button type="button" variant="primary" onClick={() => void q.refetch()}>Retry</Button>}</div></Alert>;
  }

  const toggle = (name: string, on: boolean) => setChosen((old) => {
    const next = new Set(picked);
    if (on) next.add(name); else next.delete(name);
    return { ...old, [inst.account]: next };
  });
  const addOnGitHub = async () => {
    setErr(null);
    try {
      const r = await addGitHubRepos(slug, connectorId, currentReturnPath());
      if (r.same_tab) window.location.assign(r.url);
      else window.open(r.url, '_blank', 'noopener');
    } catch { setErr('GitHub could not be opened. Try again.'); }
  };
  const save = async () => {
    if (busy || picked.size === 0) return;
    setBusy(true); setErr(null);
    try {
      const r = await saveGitHubRepos(slug, connectorId, inst.account, [...picked]);
      onSaved(r.connector_id, r.result);
    } catch (e) {
      setErr(e instanceof ApiError && e.status === 404 ? 'This GitHub sign-in expired. Connect GitHub again.' : 'The repositories could not be saved. Try again.');
    } finally { setBusy(false); }
  };
  // The app's install flow returns here after saving on GitHub; the fallback opens a new tab and this list refreshes on return.
  const addLink = data.flow === 'install'
    ? <Button type="button" variant="ghost" onClick={() => void addOnGitHub()}>Add repositories on GitHub</Button>
    : <ExtLink href={inst.settings_url} variant="ghost">Add repositories on GitHub</ExtLink>;
  const repos = inst.repositories.filter((r) => !filter.trim() || r.full_name.toLowerCase().includes(filter.trim().toLowerCase()));

  // a11y review (Blocker): Enter on a checkbox or radio would submit the onboarding form around the picker.
  return <section className="gh-pick" aria-labelledby={headingId} data-part="github-picker"
    onKeyDown={(e) => { if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') e.preventDefault(); }}>
    <h2 id={headingId} className="gh-pick-h">Pick repositories to scan</h2>
    {data.installations.length > 1 && <fieldset className="gh-pick-set">
      <legend>GitHub account</legend>
      {data.installations.map((i) => <Radio key={i.account} id={`gh-acct-${i.account}`} name="github-account" checked={i.account === inst.account}
        onChange={() => setAccount(i.account)} label={i.account} />)}
    </fieldset>}
    {inst.repositories.length === 0 ? <div className="su-acts">
      <p className="su-hint">{`The GAC Pro app on ${inst.account} can't read any repositories yet.`}</p>{addLink}
    </div> : <fieldset className="gh-pick-set">
      <legend>{`Repositories on ${inst.account}`}</legend>
      {inst.repositories.length > 10 && <Field id="gh-filter" label="Find a repository"><Input value={filter} onChange={(e) => setFilter(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} /></Field>}
      <div className="gh-pick-list">{repos.map((r) => <Checkbox key={r.full_name} id={`gh-repo-${r.full_name}`} checked={picked.has(r.full_name)}
        onChange={(e) => toggle(r.full_name, e.target.checked)} label={r.full_name.slice(inst.account.length + 1)} />)}</div>
      {repos.length === 0 && <p className="su-hint">No repository matches. Clear the search, or add it on GitHub.</p>}
    </fieldset>}
    <p className="su-hint">{"Don't see a repository? Add it to the GAC Pro app on GitHub, then pick it here."}</p>
    {err && <p role="alert" className="gac-reconnect-err">{err}</p>}
    <div className="su-acts">
      {inst.repositories.length > 0 && <Button type="button" variant="primary" aria-disabled={busy || picked.size === 0 || undefined} aria-busy={busy || undefined}
        onClick={() => void save()}>{busy ? 'Saving…' : picked.size === 0 ? 'Scan repositories' : `Scan ${picked.size} ${picked.size === 1 ? 'repository' : 'repositories'}`}</Button>}
      {inst.repositories.length > 0 && <span className="su-hint" aria-live="polite">{`${picked.size} of ${inst.repositories.length} picked`}</span>}
      {addLink}
      {onCancel && <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>}
    </div>
  </section>;
}
